import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Wallet, User, Phone, Loader2, AlertCircle, CheckCircle2, Clock, ArrowDownToLine, Banknote } from "lucide-react";
import { BottomNav } from "@/components/BottomNav";
import { useAuth } from "@/hooks/useAuth";
import { analyticsService } from "@/services/analytics";
import directus from "@/lib/directus";
import { readItems, createItem, customEndpoint } from "@directus/sdk";
import { safeBack } from "@/lib/utils";
import { toast } from "sonner";

const MIN_WITHDRAWAL = 100;


export function AffiliateWithdrawPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingBalance, setIsFetchingBalance] = useState(true);
  const [availableBalance, setAvailableBalance] = useState(0);
  const [relationId, setRelationId] = useState<string | null>(null);
  const [pendingWithdrawals, setPendingWithdrawals] = useState(0);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [formData, setFormData] = useState({ phoneNumber: "", displayName: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [done, setDone] = useState(false);
  const amountInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    analyticsService.trackScreenView("affiliate_withdraw_page");
    loadBalance();
  }, []);

  const loadBalance = async () => {
    setIsFetchingBalance(true);
    try {
      const numero = user?.phone_number;
      if (!numero) {
        setAvailableBalance(0);
        return;
      }

      const [relations, pending] = await Promise.all([
        directus.request(
          readItems("commercial_relations", {
            filter: { commercial_phoneNumber: { _eq: numero } },
            fields: ["id", "balance", "commission", "commercial_phoneNumber"],
            limit: 1,
          })
        ),
        directus.request(
          readItems("retrait_commission", {
            filter: {
              phone: { _eq: numero },
              status: { _eq: "pending" },
            },
            aggregate: { sum: ["amount"] },
          } as any)
        ).catch(() => []),
      ]);

      const relation = Array.isArray(relations) && relations.length > 0 ? (relations[0] as any) : null;
      const balance = relation?.balance ?? user?.balance ?? 0;
      setAvailableBalance(balance);
      setRelationId(relation?.id ?? null);

      const pendingSum = Array.isArray(pending) && pending[0]?.sum?.amount
        ? Number(pending[0].sum.amount)
        : 0;
      setPendingWithdrawals(pendingSum);
    } catch (error) {
      console.error("Error loading balance:", error);
      toast.error("Erreur lors du chargement du solde");
    } finally {
      setIsFetchingBalance(false);
    }
  };

  const handleAmountChange = (raw: string) => {
    // Accepte seulement les chiffres
    const digits = raw.replace(/\D/g, "");
    setWithdrawAmount(digits);
    if (errors.amount) setErrors((prev) => ({ ...prev, amount: "" }));
  };

  const handleMax = () => {
    setWithdrawAmount(String(availableBalance));
    if (errors.amount) setErrors((prev) => ({ ...prev, amount: "" }));
    amountInputRef.current?.focus();
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    const amount = Number(withdrawAmount);

    if (!withdrawAmount || isNaN(amount) || amount <= 0) {
      newErrors.amount = "Veuillez saisir un montant";
    } else if (amount < MIN_WITHDRAWAL) {
      newErrors.amount = `Minimum ${MIN_WITHDRAWAL.toLocaleString("fr-FR")} FCFA`;
    } else if (amount > availableBalance) {
      newErrors.amount = `Solde insuffisant (max ${availableBalance.toLocaleString("fr-FR")} FCFA)`;
    }

    if (!formData.displayName.trim()) {
      newErrors.displayName = "Nom complet requis";
    } else if (formData.displayName.trim().length < 3) {
      newErrors.displayName = "Minimum 3 caractères";
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Numéro requis";
    } else if (!/^[+]?[0-9]{8,}$/.test(formData.phoneNumber.replace(/\s/g, ""))) {
      newErrors.phoneNumber = "Format invalide (ex : 70 00 00 00)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const amount = Number(withdrawAmount);
    // phone = numéro du commercial → matche commercial_phoneNumber côté backend
    const phone = user?.phone_number ?? "";

    setIsLoading(true);
    try {
      // Identique à mobcash transactionService.withdrawMoney
      // user_id = numéro du commercial → matche commercial_phoneNumber côté backend
      // comercial_id non envoyé → évite le crash UUID sur le filtre user_id
      const phone = user?.phone_number ?? "";

      const response = await directus.request(
        customEndpoint({
          path: "/pay/commercial-cashout",
          method: "POST",
          body: JSON.stringify({
            amount,
            phone_number: formData.phoneNumber.trim(),
            name: formData.displayName.trim(),
            user_id: phone,
          }),
        })
      );

      if (!response) {
        throw new Error("Réponse vide du serveur");
      }

      // Log dans Retrait_Commercial (même table que mobcash)
      await directus.request(
        createItem("Retrait_Commercial", {
          phone_number: formData.phoneNumber.trim(),
          Commercial_id: phone,
          montant: amount,
          nom: formData.displayName.trim(),
        } as any)
      ).catch((err) => {
        console.warn("Could not log Retrait_Commercial:", err);
      });

      setAvailableBalance((prev) => Math.max(0, prev - amount));
      setPendingWithdrawals((prev) => prev + amount);
      setDone(true);

      toast.success("Retrait effectué !", { duration: 5000 });
    } catch (error: any) {
      console.error("Error during withdrawal:", error);
      toast.error(error.message || "Erreur lors du retrait. Veuillez réessayer.");
    } finally {
      setIsLoading(false);
    }
  };

  const canWithdraw = availableBalance >= MIN_WITHDRAWAL;
  const parsedAmount = Number(withdrawAmount) || 0;

  return (
    <div className="relative flex min-h-[100dvh] flex-col bg-background text-foreground [padding-top:env(safe-area-inset-top)]">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10"
        style={{
          background:
            "radial-gradient(120% 80% at 50% 0%, var(--page-gradient-via) 0%, var(--page-gradient-from) 45%, var(--page-gradient-to) 100%)",
        }}
      />

      <header className="sticky top-0 z-30 border-b border-border bg-header-bg backdrop-blur-xl">
        <div className="mx-auto flex max-w-[52ch] items-center gap-3 px-4 py-3 lg:max-w-6xl lg:px-8">
          <button
            onClick={() => safeBack(navigate, "/parrainage")}
            aria-label="Retour"
            className="grid size-9 place-items-center rounded-full bg-muted ring-1 ring-card-muted-ring"
          >
            <ArrowLeft className="size-4" />
          </button>
          <div className="flex items-center gap-2">
            <Wallet className="size-4 text-accent-brand" />
            <h1 className="text-base font-semibold tracking-tight">Retrait des gains</h1>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[52ch] flex-1 space-y-4 px-4 py-4 pb-24 lg:max-w-6xl lg:px-8">

        {/* Solde disponible */}
        <section
          className="relative overflow-hidden rounded-2xl p-5 text-white ring-1 ring-black/10"
          style={{ background: "linear-gradient(135deg, #002366 0%, #0052cc 100%)" }}
        >
          {isFetchingBalance ? (
            <div className="flex items-center gap-3">
              <Loader2 className="size-5 animate-spin text-white/70" />
              <p className="text-sm text-white/80">Chargement du solde…</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <span className="grid size-11 shrink-0 place-items-center rounded-2xl bg-white/25">
                  <Wallet className="size-5" />
                </span>
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/80">
                    Solde disponible
                  </p>
                  <p className="mt-0.5 text-3xl font-black tabular-nums">
                    {availableBalance.toLocaleString("fr-FR")}{" "}
                    <span className="text-base font-semibold">FCFA</span>
                  </p>
                </div>
              </div>

              {pendingWithdrawals > 0 && (
                <div className="mt-3 flex items-center justify-between rounded-xl bg-white/15 px-3 py-2.5 ring-1 ring-white/20">
                  <span className="flex items-center gap-2 text-[12px] text-white/90">
                    <Clock className="size-3.5" /> En attente de traitement
                  </span>
                  <span className="text-sm font-bold tabular-nums">
                    {pendingWithdrawals.toLocaleString("fr-FR")} FCFA
                  </span>
                </div>
              )}
            </>
          )}
        </section>

        {/* Solde insuffisant */}
        {!isFetchingBalance && !canWithdraw && (
          <div className="flex items-start gap-2.5 rounded-2xl bg-destructive/10 p-3.5 ring-1 ring-destructive/30">
            <AlertCircle className="size-4 shrink-0 text-destructive mt-0.5" />
            <p className="text-[12px] text-foreground">
              Solde insuffisant. Minimum requis : {MIN_WITHDRAWAL.toLocaleString("fr-FR")} FCFA.
            </p>
          </div>
        )}

        {/* Confirmation */}
        {done ? (
          <section className="rounded-2xl bg-card-muted p-5 text-center ring-1 ring-card-muted-ring">
            <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-accent-brand-soft">
              <CheckCircle2 className="size-6 text-accent-brand" />
            </span>
            <p className="mt-3 text-sm font-semibold">Demande envoyée !</p>
            <p className="mt-1 text-[12px] text-text-subtle">
              Votre retrait de {parsedAmount.toLocaleString("fr-FR")} FCFA est en cours de traitement.
            </p>
            <button
              onClick={() => navigate("/parrainage")}
              className="mt-4 inline-flex items-center gap-2 rounded-xl bg-accent-brand px-4 py-2.5 text-sm font-semibold text-white transition active:scale-95"
            >
              Retour aux filleuls
            </button>
          </section>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 rounded-2xl bg-card-muted p-4 ring-1 ring-card-muted-ring">
            <p className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-accent-brand">
              <ArrowDownToLine className="size-3.5" /> Informations de retrait
            </p>

            {/* ─── Montant ─── */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label htmlFor="withdrawAmount" className="text-[11px] font-semibold text-text-subtle">
                  Montant à retirer
                </label>
                <button
                  type="button"
                  onClick={handleMax}
                  disabled={!canWithdraw}
                  className="rounded-full bg-accent-brand-soft px-2.5 py-0.5 text-[10px] font-bold text-accent-brand transition active:scale-95 disabled:opacity-40"
                >
                  MAX
                </button>
              </div>

              {/* Input montant avec suffixe FCFA */}
              <div
                className={`flex items-center gap-2 rounded-xl bg-background px-3 py-2.5 ring-1 transition focus-within:ring-2 ${
                  errors.amount
                    ? "ring-destructive focus-within:ring-destructive"
                    : "ring-card-muted-ring focus-within:ring-accent-brand"
                }`}
              >
                <Banknote className="size-4 shrink-0 text-text-subtle" />
                <input
                  ref={amountInputRef}
                  id="withdrawAmount"
                  type="text"
                  inputMode="numeric"
                  placeholder="0"
                  value={withdrawAmount ? Number(withdrawAmount).toLocaleString("fr-FR") : ""}
                  onChange={(e) => handleAmountChange(e.target.value.replace(/\s/g, ""))}
                  disabled={isLoading || !canWithdraw}
                  className="min-w-0 flex-1 bg-transparent text-base font-semibold tabular-nums outline-none placeholder:text-text-subtle disabled:opacity-50"
                />
                <span className="shrink-0 text-[11px] font-semibold text-text-subtle">FCFA</span>
              </div>

              {errors.amount && (
                <p className="text-[11px] text-destructive">{errors.amount}</p>
              )}

            </div>

            {/* ─── Résumé montant ─── */}
            {parsedAmount >= MIN_WITHDRAWAL && parsedAmount <= availableBalance && (
              <div className="rounded-xl bg-background px-3 py-2.5 ring-1 ring-card-muted-ring space-y-1">
                <div className="flex justify-between text-[11px] text-text-subtle">
                  <span>Solde actuel</span>
                  <span className="tabular-nums font-medium text-foreground">
                    {availableBalance.toLocaleString("fr-FR")} FCFA
                  </span>
                </div>
                <div className="flex justify-between text-[11px] text-text-subtle">
                  <span>Montant demandé</span>
                  <span className="tabular-nums font-semibold text-accent-brand">
                    − {parsedAmount.toLocaleString("fr-FR")} FCFA
                  </span>
                </div>
                <div className="mt-1 border-t border-border pt-1 flex justify-between text-[11px]">
                  <span className="font-semibold text-foreground">Solde restant</span>
                  <span className="tabular-nums font-bold text-foreground">
                    {Math.max(0, availableBalance - parsedAmount).toLocaleString("fr-FR")} FCFA
                  </span>
                </div>
              </div>
            )}

            {/* ─── Nom complet ─── */}
            <div className="space-y-1.5">
              <label htmlFor="displayName" className="text-[11px] font-semibold text-text-subtle">
                Nom complet (Mobile Money)
              </label>
              <div
                className={`flex items-center gap-2 rounded-xl bg-background px-3 py-2.5 ring-1 transition focus-within:ring-2 ${
                  errors.displayName
                    ? "ring-destructive focus-within:ring-destructive"
                    : "ring-card-muted-ring focus-within:ring-accent-brand"
                }`}
              >
                <User className="size-4 shrink-0 text-text-subtle" />
                <input
                  id="displayName"
                  type="text"
                  placeholder="Nom sur le compte Mobile Money"
                  value={formData.displayName}
                  onChange={(e) => handleInputChange("displayName", e.target.value)}
                  disabled={isLoading || !canWithdraw}
                  className="min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-text-subtle disabled:opacity-50"
                />
              </div>
              {errors.displayName && (
                <p className="text-[11px] text-destructive">{errors.displayName}</p>
              )}
            </div>

            {/* ─── Numéro de téléphone ─── */}
            <div className="space-y-1.5">
              <label htmlFor="phoneNumber" className="text-[11px] font-semibold text-text-subtle">
                Numéro Mobile Money (qui reçoit)
              </label>
              <div
                className={`flex items-center gap-2 rounded-xl bg-background px-3 py-2.5 ring-1 transition focus-within:ring-2 ${
                  errors.phoneNumber
                    ? "ring-destructive focus-within:ring-destructive"
                    : "ring-card-muted-ring focus-within:ring-accent-brand"
                }`}
              >
                <Phone className="size-4 shrink-0 text-text-subtle" />
                <input
                  id="phoneNumber"
                  type="tel"
                  inputMode="tel"
                  placeholder="Ex : 70 00 00 00"
                  value={formData.phoneNumber}
                  onChange={(e) => handleInputChange("phoneNumber", e.target.value)}
                  disabled={isLoading || !canWithdraw}
                  className="min-w-0 flex-1 bg-transparent text-sm tabular-nums outline-none placeholder:text-text-subtle disabled:opacity-50"
                />
              </div>
              {errors.phoneNumber && (
                <p className="text-[11px] text-destructive">{errors.phoneNumber}</p>
              )}
            </div>

            {/* ─── Bouton soumettre ─── */}
            <button
              type="submit"
              disabled={isLoading || !canWithdraw || isFetchingBalance}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-accent-brand py-3.5 text-sm font-bold text-white transition active:scale-[0.98] disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <Loader2 className="size-4 animate-spin" /> Traitement…
                </>
              ) : (
                <>
                  <ArrowDownToLine className="size-4" />
                  {parsedAmount >= MIN_WITHDRAWAL
                    ? `Retirer ${parsedAmount.toLocaleString("fr-FR")} FCFA`
                    : "Demander le retrait"}
                </>
              )}
            </button>

            <p className="text-center text-[10px] text-text-subtle">
              Orange Money / Moov Money
            </p>
          </form>
        )}

        {/* Info */}
        <div className="rounded-2xl bg-card-muted p-4 ring-1 ring-card-muted-ring space-y-2">
          <p className="text-[10px] font-bold uppercase tracking-widest text-text-subtle">
            Informations
          </p>
          {[
            ["Minimum de retrait", `${MIN_WITHDRAWAL.toLocaleString("fr-FR")} FCFA`],
            ["Méthode", "Orange Money · Moov Money"],
          ].map((row) => (
            <div key={row[0]} className="flex items-center justify-between">
              <span className="text-[12px] text-text-subtle">{row[0]}</span>
              <span className="text-[12px] font-semibold text-foreground">{row[1]}</span>
            </div>
          ))}
        </div>
      </main>

      <BottomNav />
    </div>
  );
}

export default AffiliateWithdrawPage;
